import { findOneElement } from "~/core/dom/traversal";
import { atShopifyRoot } from "~/core/network/shopify-root";

type CartDrawerSections = {
    cart: string;
};

type CartDrawerElement = HTMLElement & {
    renderContents?: (parsedState: {
        id: string | number | null;
        sections: CartDrawerSections;
    }) => void;
    setActiveElement?: (element: Element | null) => void;
};

type DrawerElement = HTMLElement & {
    open?: () => void;
};

type RebuySmartCartWindow = Window & {
    Rebuy?: {
        SmartCart?: {
            show?: () => void;
        };
    };
};

const isCartDrawerSections = (
    sections: unknown,
): sections is CartDrawerSections => {
    if (sections == null || typeof sections !== "object") {
        return false;
    }

    return typeof (sections as Record<string, unknown>).cart === "string";
};

const getCartDrawer = (): CartDrawerElement | null => {
    const cartDrawer = document.querySelector("cart-drawer");

    if (!(cartDrawer instanceof HTMLElement)) {
        return null;
    }

    return cartDrawer as CartDrawerElement;
};

const getDrawerPanel = (): DrawerElement | null => {
    const drawerPanel = findOneElement(document, "#CartDrawer");

    if (!(drawerPanel instanceof HTMLElement)) {
        return null;
    }

    return drawerPanel as DrawerElement;
};

export const refreshCartDrawerContents = async (): Promise<void> => {
    const cartDrawer = getCartDrawer();

    if (typeof cartDrawer?.renderContents !== "function") {
        return;
    }

    if (typeof cartDrawer.setActiveElement === "function") {
        cartDrawer.setActiveElement(document.activeElement);
    }

    const response = await fetch(atShopifyRoot("/?sections=cart"), {
        headers: {
            Accept: "application/json",
        },
    });

    if (!response.ok) {
        throw new Error(`Failed to refresh cart drawer: ${response.status}`);
    }

    const sections: unknown = await response.json();

    if (!isCartDrawerSections(sections)) {
        throw new Error("Failed to refresh cart drawer: invalid cart section");
    }

    cartDrawer.renderContents({
        id: null,
        sections,
    });
};

export const openCartDrawer = (): void => {
    const showRebuySmartCart = (window as RebuySmartCartWindow).Rebuy
        ?.SmartCart?.show;

    if (typeof showRebuySmartCart === "function") {
        showRebuySmartCart();
        return;
    }

    const drawerPanel = getDrawerPanel();

    if (typeof drawerPanel?.open === "function") {
        drawerPanel.open();
    }
};
